import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Loader2 } from 'lucide-react';
import { settingsStore, OpeningHourDay } from '@/services/apiService';
import { useState, useEffect } from 'react';

const Footer = () => {
  const { language, t } = useLanguage();
  const location = useLocation();
  const isLegalPage = location.pathname === '/privacy-policy' || location.pathname === '/regulamin';
  const [settings, setSettings] = useState(settingsStore.get());
  const [events, setEvents] = useState<any[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    // Helper to parse events
    const parseEvents = (data: any) => {
      if (!data) return [];
      try {
        return typeof data === 'string' ? JSON.parse(data) : data;
      } catch {
        return [];
      }
    };

    // Initial fetch
    const currentSettings = settingsStore.get();
    setSettings(currentSettings);
    setEvents(Array.isArray(parseEvents(currentSettings.events)) ? parseEvents(currentSettings.events) : []);

    // Subscribe to changes
    const unsubscribe = settingsStore.subscribe((newSettings) => {
      setSettings(newSettings);
      setEvents(Array.isArray(parseEvents(newSettings.events)) ? parseEvents(newSettings.events) : []);
    });

    return () => unsubscribe();
  }, []);

  const content = {
    PL: {
      address: 'ul. Winiarska 15, 00-001 Warszawa',
      hours: [
        { day: 'Poniedziałek - Piątek', time: '10:00 - 19:00' },
        { day: 'Sobota', time: '10:00 - 16:00' },
        { day: 'Niedziela', time: 'Zamknięte' },
      ],
    },
    EN: {
      address: '15 Winiarska St., 00-001 Warsaw',
      hours: [
        { day: 'Monday - Friday', time: '10:00 - 19:00' },
        { day: 'Saturday', time: '10:00 - 16:00' },
        { day: 'Sunday', time: 'Closed' },
      ],
    },
  };

  const c = content[language];

  return (
    <footer id="contact" className="bg-foreground text-primary-foreground">
      {/* Events banner */}
      {!isLegalPage && events && events.length > 0 && (
        <div className="border-b border-primary-foreground/10 bg-primary py-8">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="mb-6 text-center font-display text-2xl font-bold text-primary-foreground">
                {t('events')}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                {events.map((event, index) => (
                  <div
                    key={index}
                    className="flex-1 w-full sm:min-w-[200px] max-w-sm rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 p-4 text-center"
                  >
                    <p className="mb-1 font-medium text-primary-foreground">{event.title}</p>
                    <p className="text-sm text-primary-foreground/70">{event.date}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Main footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid gap-8 lg:gap-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-4 font-display text-2xl font-bold tracking-widest uppercase">Włoska Piwnica</h2>
            <p className="mb-6 text-sm text-primary-foreground/70 leading-relaxed">
              {language === 'PL'
                ? 'Autentyczne włoskie wina i produkty najwyższej jakości. Tradycja od 1937 roku.'
                : 'Authentic Italian wines and premium products. Tradition since 1937.'}
            </p>

            {/* Social */}
            <div className="flex gap-3">
              <a
                href={settings.facebook_link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/20 transition-colors hover:bg-primary-foreground/10"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={settings.instagram_link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/20 transition-colors hover:bg-primary-foreground/10"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
            <p className="mt-3 text-sm text-primary-foreground/70">{t('followUs')}</p>
          </motion.div>

          {/* Contact */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="mb-6 font-display text-lg font-semibold">{t('contact')}</h3>
            <address className="space-y-4 not-italic">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                <p className="text-sm text-primary-foreground/80">{settings.address || c.address}</p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 flex-shrink-0 text-accent" />
                <a href={`tel:${settings.phone.replace(/\s/g, '')}`} className="text-sm text-primary-foreground/80 hover:text-primary-foreground">
                  {settings.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 flex-shrink-0 text-accent" />
                <a href={`mailto:${settings.email}`} className="text-sm text-primary-foreground/80 hover:text-primary-foreground">
                  {settings.email}
                </a>
              </div>
            </address>
          </motion.div>

          {/* Categories */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
          >
            <h3 className="mb-6 font-display text-lg font-semibold">
              {language === 'PL' ? 'Oferta' : 'Products'}
            </h3>
            <div className="flex flex-col space-y-3">
              <Link to="/produkty/wina" className="text-sm text-primary-foreground/80 hover:text-primary-foreground hover:underline transition-colors">
                {language === 'PL' ? 'Wina z beczki' : 'Wines on tap'}
              </Link>
              <Link to="/produkty/oliwa" className="text-sm text-primary-foreground/80 hover:text-primary-foreground hover:underline transition-colors">
                {language === 'PL' ? 'Włoska oliwa' : 'Italian Olive Oil'}
              </Link>
              <Link to="/produkty/tradycyjne-przekaski" className="text-sm text-primary-foreground/80 hover:text-primary-foreground hover:underline transition-colors">
                {language === 'PL' ? 'Tradycyjne przekąski' : 'Traditional Snacks'}
              </Link>
              <Link to="/produkty/zestawy" className="text-sm text-primary-foreground/80 hover:text-primary-foreground hover:underline transition-colors">
                {language === 'PL' ? 'Zestawy prezentowe' : 'Gift Sets'}
              </Link>
              <Link to="/produkty/pozostale-produkty" className="text-sm text-primary-foreground/80 hover:text-primary-foreground hover:underline transition-colors">
                {language === 'PL' ? 'Pozostałe produkty' : 'Other Products'}
              </Link>
            </div>
          </motion.div>

          {/* Hours */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="mb-6 font-display text-lg font-semibold">{t('openingHours')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-8 gap-y-3">
              {(() => {
                let hours: OpeningHourDay[] = [];
                if (settings.opening_hours) {
                  if (typeof settings.opening_hours === 'string') {
                    try {
                      hours = JSON.parse(settings.opening_hours);
                    } catch (e) {
                      return (
                        <div className="flex items-start gap-3 col-span-full">
                          <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                          <p className="text-sm text-primary-foreground/60 whitespace-pre-line">{settings.opening_hours}</p>
                        </div>
                      );
                    }
                  } else {
                    hours = settings.opening_hours;
                  }
                }

                if (hours.length > 0) {
                  const grouped: { start: string; end: string; open: string; close: string; closed: boolean }[] = [];
                  let current = { start: hours[0].day, end: hours[0].day, open: hours[0].open, close: hours[0].close, closed: hours[0].closed };

                  for (let i = 1; i < hours.length; i++) {
                    const h = hours[i];
                    if (h.open === current.open && h.close === current.close && h.closed === current.closed) {
                      current.end = h.day;
                    } else {
                      grouped.push(current);
                      current = { start: h.day, end: h.day, open: h.open, close: h.close, closed: h.closed };
                    }
                  }
                  grouped.push(current);

                  return grouped.map((g, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-primary-foreground/40">
                          {g.start === g.end ? t(`day_${g.start}`) : `${t(`day_${g.start}`)} - ${t(`day_${g.end}`)}`}
                        </p>
                        <p className="text-sm text-primary-foreground/80">
                          {g.closed ? t('closed') : `${g.open} - ${g.close}`}
                        </p>
                      </div>
                    </div>
                  ));
                }

                return c.hours.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-primary-foreground/40">{item.day}</p>
                      <p className="text-sm text-primary-foreground/80">{item.time}</p>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "200px" }}
            onViewportEnter={() => setMapLoaded(true)}
            transition={{ delay: 0.3 }}
          >
            <h3 className="mb-6 font-display text-lg font-semibold">
              {language === 'PL' ? 'Lokalizacja' : 'Location'}
            </h3>
            <div className="w-full aspect-[4/3] sm:aspect-video overflow-hidden rounded-lg bg-primary-foreground/10">
              {settings.google_maps_link ? (
                mapLoaded ? (
                  <iframe
                    src={settings.google_maps_link}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Google Maps"
                  ></iframe>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary-foreground/50" />
                  </div>
                )
              ) : (
                /* Google Maps placeholder */
                <div className="flex h-full w-full items-center justify-center">
                  <div className="text-center">
                    <MapPin className="mx-auto mb-2 h-8 w-8 text-primary-foreground/50" />
                    <p className="text-xs text-primary-foreground/50">Google Maps</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-4 py-6 md:flex-row">
          <p className="text-sm text-primary-foreground/50">
            © {new Date().getFullYear()} Włoska piwnica. {language === 'PL' ? 'Wszelkie prawa zastrzeżone.' : 'All rights reserved.'}
          </p>
          <div className="flex gap-6">
            <Link to="/regulamin" className="text-sm text-primary-foreground/50 transition-colors hover:text-primary-foreground">
              {t('terms')}
            </Link>
            <Link to="/privacy-policy" className="text-sm text-primary-foreground/50 transition-colors hover:text-primary-foreground">
              {t('privacyPolicy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
